import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ManifestoRotinaSchema, type ManifestoRotina } from '@remoto/protocolo';
import { pastaCurta, type CelulaDaMatriz, type RotinaDetalhe, type VersaoDaRotina } from '@remoto/shared';
import { Cartao } from '../componentes/layout';
import { CampoDeEscolha } from '../componentes/campos';
import { Modal, usarConfirmacao } from '../componentes/Modal';
import { IconeAnterior, IconeEditar } from '../componentes/icones';
import { TextoMarkdown } from '../componentes/chat/TextoMarkdown';
import { mensagemDoErro } from '../erros';
import { tempoRelativo } from '../formato';
import { GESTOS, usarAlvos, usarGesto, usarPropostas, usarRotina } from './consultas';
import { CartaoDeProposta, OrigemDaProposta } from './CartaoDeProposta';
import {
  AvisoDeErro,
  BotaoPrimario,
  BotaoSecundario,
  CampoDeTextoLongo,
  DialogoDeMotivo,
  usarPodeComandar,
} from './pecas';
import {
  hashCurto,
  historicoEmOrdem,
  ROTULO_DA_FORMA,
  rotuloDoResultado,
  situacaoDaVersao,
  type SituacaoDaVersao,
} from './regras';
import { SeloDaClasse, SeloDoEstado, SeloNeutro } from './selos';

/**
 * O detalhe de uma rotina (F3.7): as propostas dela esperando decisão, a
 * matriz por alvo, a versão vigente e o histórico — "superada por" com o
 * motivo, e a conversa de origem.
 *
 * Ordem da tela, de cima para baixo: o que pede uma decisão (propostas), o que
 * diz se dá para confiar (matriz), o que roda (vigente), e de onde veio
 * (histórico). Quem chega aqui por "Revisar" cai direto na proposta.
 */

function DataEResultado({ evidencia, vazio }: { evidencia: { em: string; resultado: string } | null; vazio: string }): React.JSX.Element {
  if (!evidencia) return <span className="text-zinc-500">{vazio}</span>;
  return (
    <span className={evidencia.resultado === 'FALHOU' ? 'text-amber-300' : 'text-zinc-300'} title={new Date(evidencia.em).toLocaleString('pt-BR')}>
      {rotuloDoResultado(evidencia.resultado)} · {tempoRelativo(evidencia.em)}
    </span>
  );
}

/** Uma linha da matriz: estado, última real, última sonda e o gesto de marcar. */
function LinhaDaMatriz({
  celula,
  semSonda,
  podeComandar,
  aoMarcar,
}: {
  celula: CelulaDaMatriz;
  semSonda: boolean;
  podeComandar: boolean;
  aoMarcar: (alvo: string, marca: 'QUEBRADA' | 'REVISADA') => void;
}): React.JSX.Element {
  return (
    <li
      data-testid="linha-da-matriz"
      data-alvo={celula.alvo}
      className="grid min-w-0 grid-cols-2 gap-x-3 gap-y-1.5 rounded-lg border border-zinc-800/70 p-3 text-xs lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1.3fr)_minmax(0,1.3fr)_auto] lg:items-center lg:rounded-none lg:border-0 lg:border-t lg:px-2 lg:py-2"
    >
      <span className="col-span-2 min-w-0 truncate font-mono text-sm text-zinc-100 lg:col-span-1">{celula.alvo}</span>
      <span className="min-w-0">
        <SeloDoEstado estado={celula.estado} />
      </span>
      <span className="min-w-0 truncate">
        <span className="text-zinc-600 lg:sr-only">última real: </span>
        <DataEResultado evidencia={celula.ultimaReal} vazio="nenhum caso real" />
      </span>
      <span className="min-w-0 truncate">
        <span className="text-zinc-600 lg:sr-only">última sonda: </span>
        <DataEResultado evidencia={celula.ultimaSonda} vazio={semSonda ? 'sem sonda' : 'nenhuma ainda'} />
      </span>
      <span className="col-span-2 min-w-0 lg:col-span-1 lg:text-right">
        {podeComandar ? (
          celula.estado === 'quebrada' ? (
            <button
              type="button"
              onClick={() => aoMarcar(celula.alvo, 'REVISADA')}
              className="rounded-md border border-zinc-700 px-2 py-1 text-xs text-zinc-200 transition hover:bg-zinc-800 pointer-coarse:min-h-11"
            >
              Desmarcar quebra
            </button>
          ) : (
            <button
              type="button"
              onClick={() => aoMarcar(celula.alvo, 'QUEBRADA')}
              className="rounded-md border border-red-900 px-2 py-1 text-xs text-red-200 transition hover:bg-red-950/50 pointer-coarse:min-h-11"
            >
              Marcar quebrada
            </button>
          )
        ) : null}
      </span>
    </li>
  );
}

function FrasesDaSituacao({ situacao }: { situacao: SituacaoDaVersao }): React.JSX.Element {
  switch (situacao.tipo) {
    case 'vigente':
      return <SeloNeutro tom="verde">vigente</SeloNeutro>;
    case 'pendente':
      return <SeloNeutro tom="azul">esperando decisão</SeloNeutro>;
    case 'recusada':
      return (
        <span className="min-w-0 text-xs text-zinc-400">
          <SeloNeutro tom="vermelho">recusada</SeloNeutro>
          {situacao.motivo ? <span className="ml-1.5">— {situacao.motivo}</span> : null}
        </span>
      );
    case 'superada':
      return (
        <span className="min-w-0 text-xs text-zinc-400" data-testid="superada-por">
          <SeloNeutro>superada</SeloNeutro>
          {situacao.por ? (
            <span className="ml-1.5">
              pela v{situacao.por.numero} — {situacao.por.motivo}
            </span>
          ) : null}
        </span>
      );
    default: {
      const nenhuma: never = situacao;
      return nenhuma;
    }
  }
}

function Historico({ rotina }: { rotina: RotinaDetalhe }): React.JSX.Element {
  const vigenteId = rotina.vigente?.id ?? null;
  return (
    <ol className="max-h-[50vh] space-y-2 overflow-y-auto pr-1" data-testid="historico">
      {historicoEmOrdem(rotina.versoes).map((v) => (
        <li key={v.id} data-testid="versao-no-historico" data-numero={v.numero} className="min-w-0 rounded-lg border border-zinc-800/70 p-3">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-mono text-sm text-zinc-100">v{v.numero}</span>
            <span className="font-mono text-xs text-zinc-500" title={v.sha256}>
              {hashCurto(v.sha256)}
            </span>
            <FrasesDaSituacao situacao={situacaoDaVersao(v, vigenteId, rotina.versoes)} />
          </div>
          <p className="mt-1 break-words text-xs text-zinc-300">{v.motivo}</p>
          <p className="mt-0.5 text-[11px] text-zinc-500">
            <OrigemDaProposta propostaPor={v.propostaPor} /> · {tempoRelativo(v.criadoEm)}
          </p>
        </li>
      ))}
    </ol>
  );
}

function Vigente({ versao, arquivos }: { versao: VersaoDaRotina; arquivos: RotinaDetalhe['arquivosDaVigente'] }): React.JSX.Element {
  const lido = ManifestoRotinaSchema.safeParse(versao.manifesto);
  const manifesto: ManifestoRotina | null = lido.success ? lido.data : null;
  const [arquivoAberto, setArquivoAberto] = useState<string | null>(null);
  return (
    <div className="space-y-3 text-sm" data-testid="vigente">
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-mono text-zinc-100">v{versao.numero}</span>
        <span className="break-all font-mono text-xs text-zinc-500" title={versao.sha256}>
          {hashCurto(versao.sha256)}
        </span>
        {versao.decisao ? (
          <span className="text-xs text-zinc-500">aprovada {tempoRelativo(versao.decisao.em)}</span>
        ) : null}
        <span className="text-xs text-zinc-500">
          · <OrigemDaProposta propostaPor={versao.propostaPor} />
        </span>
      </div>
      <div className="rounded-md bg-zinc-950/40 p-3">
        <TextoMarkdown texto={versao.descricao} />
      </div>
      {manifesto ? (
        <>
          <div>
            <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-zinc-500">Fases</h4>
            <ul className="space-y-1">
              {manifesto.fases.map((f) => (
                <li key={f.nome} className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-zinc-300">
                  <span className="font-mono text-zinc-100">{f.nome}</span>
                  <SeloDaClasse classe={f.classe} />
                  <span className="text-zinc-500">até {f.timeoutSeg} s</span>
                  {f.pontoSemRetorno ? <span className="text-red-300">ponto sem retorno: {f.pontoSemRetorno}</span> : null}
                </li>
              ))}
            </ul>
            {!manifesto.fases.some((f) => f.nome === 'sonda') ? (
              // F3.5: sem sonda, a deriva só aparece no próximo caso real.
              <p className="mt-1 text-xs text-amber-300/80">sem sonda: a deriva desta rotina só aparece no próximo caso real</p>
            ) : null}
          </div>
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
            <dt className="text-zinc-500">roda em</dt>
            <dd className="text-zinc-300">{[manifesto.onde.pc ? 'PC' : null, manifesto.onde.vps ? 'VPS' : null].filter(Boolean).join(' e ')}</dd>
            <dt className="text-zinc-500">interpretador</dt>
            <dd className="break-all font-mono text-zinc-300">
              {manifesto.interpretador} {manifesto.entrada}
            </dd>
            {manifesto.requisitos.length > 0 ? (
              <>
                <dt className="text-zinc-500">requisitos</dt>
                <dd className="break-words text-zinc-300">
                  {manifesto.requisitos
                    .map((r) => {
                      switch (r.tipo) {
                        case 'segredo':
                          return `segredo ${r.nome}`;
                        case 'servico_local':
                          return `serviço ${r.endereco}`;
                        case 'navegador':
                          return `navegador na ${r.porta}`;
                        case 'projeto_vizinho':
                          return `projeto ${r.caminho}`;
                        case 'certificado':
                        case 'extensao':
                        case 'programa':
                          return `${r.tipo} ${r.nome}`;
                        default: {
                          const nenhum: never = r;
                          return String(nenhum);
                        }
                      }
                    })
                    .join(' · ')}
                </dd>
              </>
            ) : null}
            {manifesto.recursos.length > 0 ? (
              <>
                <dt className="text-zinc-500">recursos</dt>
                <dd className="break-all font-mono text-zinc-300">{manifesto.recursos.join(', ')}</dd>
              </>
            ) : null}
          </dl>
        </>
      ) : (
        <p className="text-xs text-amber-300">O manifesto desta versão não passa no contrato atual da tela.</p>
      )}
      <div>
        <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-zinc-500">Arquivos</h4>
        <ul className="space-y-1">
          {arquivos.map((a) => (
            <li key={a.caminho} className="min-w-0">
              <button
                type="button"
                onClick={() => setArquivoAberto((atual) => (atual === a.caminho ? null : a.caminho))}
                aria-expanded={arquivoAberto === a.caminho}
                className="flex w-full min-w-0 items-center gap-2 text-left text-xs text-zinc-300 hover:text-zinc-100 pointer-coarse:min-h-11"
              >
                <span className="min-w-0 flex-1 truncate font-mono">{a.caminho}</span>
                <span className="shrink-0 text-zinc-500">{a.bytes.toLocaleString('pt-BR')} bytes</span>
              </button>
              {arquivoAberto === a.caminho ? (
                <pre className="mt-1 max-h-[40vh] overflow-auto whitespace-pre-wrap break-words rounded bg-zinc-950/70 p-2 text-[11px] text-zinc-300">
                  {a.conteudo}
                </pre>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** Estender a rotina a um alvo, pela tela: aprovada por quem estende, com a semelhança (D31). */
function DialogoDeExtensao({ rotina, aberto, aoFechar }: { rotina: RotinaDetalhe; aberto: boolean; aoFechar: () => void }): React.JSX.Element | null {
  const alvos = usarAlvos();
  const estender = usarGesto(GESTOS.estender);
  const [alvo, setAlvo] = useState<string | null>(null);
  const [semelhanca, setSemelhanca] = useState('');
  // Cada abertura começa limpa. `reset` é estável; a mutação inteira, não.
  const { reset } = estender;
  useEffect(() => {
    if (aberto) {
      setAlvo(null);
      setSemelhanca('');
      reset();
    }
  }, [aberto, reset]);
  const jaTem = new Set(rotina.extensoes.filter((e) => e.decisao?.decisao !== 'RECUSADA').map((e) => e.alvo));
  const opcoes = (alvos.data ?? [])
    .filter((a) => a.ativo && !jaTem.has(a.chave))
    .map((a) => ({ id: a.chave, rotulo: `${a.chave} — ${a.titulo}` }));
  return (
    <Modal
      titulo="Estender a um alvo"
      aberto={aberto}
      aoFechar={aoFechar}
      rodape={
        <>
          <BotaoSecundario onClick={aoFechar}>Cancelar</BotaoSecundario>
          <BotaoPrimario
            disabled={!alvo || semelhanca.trim().length === 0 || estender.isPending}
            onClick={() =>
              alvo &&
              estender.mutate({ rotinaId: rotina.id, corpo: { alvo, semelhanca: semelhanca.trim() } }, { onSuccess: aoFechar })
            }
          >
            Estender
          </BotaoPrimario>
        </>
      }
    >
      <div className="space-y-3">
        <p>
          Um fluxo validado num alvo não vale para outro sem esta decisão (D31). Diga o que é igual entre os dois. Até um
          caso real conferido no alvo novo, ali só rodam fases de leitura.
        </p>
        {opcoes.length === 0 ? (
          <p className="text-xs text-zinc-500">Não há alvo ativo a que a rotina ainda não esteja estendida.</p>
        ) : (
          <CampoDeEscolha rotulo="Alvo" valor={alvo} opcoes={opcoes} aoEscolher={setAlvo} vazio="escolha o alvo…" />
        )}
        <CampoDeTextoLongo
          rotulo="Semelhança declarada"
          value={semelhanca}
          onChange={(e) => setSemelhanca(e.target.value)}
          placeholder="ex.: mesmo acesso único (SSO), mesma tela de consulta, mesmos tipos de documento"
          maxLength={2_000}
        />
        {estender.error ? <AvisoDeErro>{mensagemDoErro(estender.error, 'Não foi possível estender agora.')}</AvisoDeErro> : null}
      </div>
    </Modal>
  );
}

/**
 * O 404 de uma rotina: no catálogo geral, `nao_encontrado` fala de caminho no
 * disco. Aqui é a rotina — que pode não existir ou estar fora do escopo, e a
 * API não diz qual dos dois (`api.md` 2).
 */
export const FRASES_DA_ROTINA: Record<string, string> = {
  nao_encontrado: 'Esta rotina não existe, ou você não tem acesso a ela.',
};

export function DetalheDaRotina({ id, propostaEmFoco }: { id: string; propostaEmFoco?: string }): React.JSX.Element {
  const podeComandar = usarPodeComandar();
  const { data: rotina, error, isLoading } = usarRotina(id);
  const propostas = usarPropostas();
  const marcar = usarGesto(GESTOS.marcar);
  const arquivar = usarGesto(GESTOS.arquivarRotina);
  const [pedirConfirmacao, dialogo] = usarConfirmacao();
  const [marcando, setMarcando] = useState<{ alvo: string; marca: 'QUEBRADA' | 'REVISADA' } | null>(null);
  const [estendendo, setEstendendo] = useState(false);

  const dela = (propostas.data ?? []).filter((p) => p.especie !== 'verbete' && p.rotina.id === id);

  // Quem chegou por "Revisar" cai na proposta, e não no topo da página.
  useEffect(() => {
    if (!propostaEmFoco || !propostas.data) return;
    document.getElementById('proposta-' + propostaEmFoco)?.scrollIntoView({ block: 'start' });
  }, [propostaEmFoco, propostas.data]);

  const vigente = rotina?.versoes.find((v) => v.id === rotina.vigente?.id) ?? null;
  const semSonda = (() => {
    const lido = vigente ? ManifestoRotinaSchema.safeParse(vigente.manifesto) : null;
    return lido?.success ? !lido.data.fases.some((f) => f.nome === 'sonda') : true;
  })();

  return (
    <div className="space-y-4">
      <Link to="/toolkit" search={{ aba: 'rotinas' }} className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200">
        <IconeAnterior tamanho={14} />
        Toolkit
      </Link>

      {isLoading ? <p className="text-sm text-zinc-500">carregando…</p> : null}
      {error ? <AvisoDeErro>{mensagemDoErro(error, 'Não foi possível carregar a rotina agora.', FRASES_DA_ROTINA)}</AvisoDeErro> : null}

      {rotina ? (
        <>
          <header className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0 space-y-1">
              <h1 className="break-words text-lg font-medium text-zinc-100">{rotina.titulo}</h1>
              <p className="flex min-w-0 flex-wrap items-center gap-1.5 text-xs text-zinc-400">
                <span className="break-all font-mono">{rotina.nome}</span>
                <SeloNeutro>{ROTULO_DA_FORMA[rotina.forma]}</SeloNeutro>
                {rotina.entidade ? <span>entidade {rotina.entidade}</span> : null}
                {rotina.operacao ? <span>· operação {rotina.operacao}</span> : null}
                {rotina.projeto ? (
                  <span title={rotina.projeto.cwdProjeto}>· projeto {pastaCurta(rotina.projeto.cwdProjeto)}</span>
                ) : (
                  <span>· da frota</span>
                )}
                {rotina.arquivada ? <SeloNeutro>arquivada</SeloNeutro> : null}
              </p>
            </div>
            {podeComandar ? (
              <div className="flex flex-wrap gap-2">
                {!rotina.arquivada ? (
                  <>
                    <Link
                      to="/toolkit/rotinas/$id/versao"
                      params={{ id: rotina.id }}
                      className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 px-3 py-1.5 text-sm text-zinc-200 transition hover:bg-zinc-800 pointer-coarse:min-h-11"
                    >
                      <IconeEditar tamanho={14} />
                      Nova versão
                    </Link>
                    <BotaoSecundario onClick={() => setEstendendo(true)}>Estender a um alvo</BotaoSecundario>
                  </>
                ) : null}
                <BotaoSecundario
                  disabled={arquivar.isPending}
                  onClick={() =>
                    pedirConfirmacao({
                      titulo: rotina.arquivada ? 'Desarquivar a rotina' : 'Arquivar a rotina',
                      texto: rotina.arquivada
                        ? 'A rotina volta ao catálogo das sessões.'
                        : 'A rotina sai do catálogo das sessões e fica só para leitura. Nada do histórico se apaga.',
                      rotuloConfirmar: rotina.arquivada ? 'Desarquivar' : 'Arquivar',
                      aoConfirmar: () => arquivar.mutate({ rotinaId: rotina.id, arquivada: !rotina.arquivada }),
                    })
                  }
                >
                  {rotina.arquivada ? 'Desarquivar' : 'Arquivar'}
                </BotaoSecundario>
              </div>
            ) : null}
          </header>
          {arquivar.error ? <AvisoDeErro>{mensagemDoErro(arquivar.error, 'Não foi possível arquivar agora.')}</AvisoDeErro> : null}

          {dela.length > 0 ? (
            <section aria-label="Propostas desta rotina" className="space-y-3">
              <h2 className="text-sm font-medium text-sky-100">Esperando decisão ({dela.length})</h2>
              {dela.map((p) => (
                <CartaoDeProposta key={p.id} proposta={p} focada={p.id === propostaEmFoco} />
              ))}
            </section>
          ) : null}

          <Cartao titulo="Estado por alvo">
            {rotina.matriz.length === 0 ? (
              <p className="text-sm text-zinc-500">A rotina não foi estendida a nenhum alvo ainda.</p>
            ) : (
              <>
                <div
                  aria-hidden="true"
                  className="hidden px-2 pb-1 text-[11px] uppercase tracking-wide text-zinc-600 lg:grid lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1.3fr)_minmax(0,1.3fr)_auto] lg:gap-3"
                >
                  <span>alvo</span>
                  <span>estado</span>
                  <span>última validação real</span>
                  <span>última sonda</span>
                  <span />
                </div>
                <ul className="max-h-[50vh] space-y-2 overflow-y-auto lg:space-y-0" data-testid="matriz">
                  {rotina.matriz.map((c) => (
                    <LinhaDaMatriz
                      key={c.alvo}
                      celula={c}
                      semSonda={semSonda}
                      podeComandar={podeComandar}
                      aoMarcar={(alvo, marca) => {
                        marcar.reset();
                        setMarcando({ alvo, marca });
                      }}
                    />
                  ))}
                </ul>
              </>
            )}
            {rotina.extensoes.length > 0 ? (
              <details className="mt-3 text-xs">
                <summary className="cursor-pointer text-zinc-400 hover:text-zinc-200">Extensões e a semelhança declarada</summary>
                <ul className="mt-2 space-y-1.5">
                  {rotina.extensoes.map((e) => (
                    <li key={e.id} className="min-w-0 rounded bg-zinc-950/40 px-2 py-1.5">
                      <span className="font-mono text-zinc-200">{e.alvo}</span>{' '}
                      <span className="text-zinc-500">
                        {e.decisao ? (e.decisao.decisao === 'APROVADA' ? 'aprovada' : 'recusada') : 'esperando decisão'}
                      </span>
                      <p className="break-words text-zinc-400">{e.semelhanca}</p>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </Cartao>

          <Cartao titulo="Versão vigente">
            {vigente ? (
              <Vigente versao={vigente} arquivos={rotina.arquivosDaVigente} />
            ) : (
              <p className="text-sm text-zinc-500">Ainda não há versão aprovada: nada desta rotina roda.</p>
            )}
          </Cartao>

          <Cartao titulo="Histórico de versões">
            <Historico rotina={rotina} />
          </Cartao>

          <DialogoDeMotivo
            titulo={marcando?.marca === 'REVISADA' ? 'Desmarcar a quebra' : 'Marcar como quebrada'}
            aberto={marcando !== null}
            explicacao={
              marcando?.marca === 'REVISADA' ? (
                <>
                  A rotina volta ao estado que as validações dizem em <span className="font-mono">{marcando.alvo}</span>.
                </>
              ) : (
                <>
                  Nada desta rotina roda em <span className="font-mono">{marcando?.alvo}</span> até alguém desmarcar, com
                  motivo. A versão não muda.
                </>
              )
            }
            rotuloDoBotao={marcando?.marca === 'REVISADA' ? 'Desmarcar' : 'Marcar quebrada'}
            perigoso={marcando?.marca === 'QUEBRADA'}
            ocupado={marcar.isPending}
            erro={marcar.error ? mensagemDoErro(marcar.error, 'Não foi possível marcar agora.') : null}
            aoFechar={() => setMarcando(null)}
            aoConfirmar={(motivo) =>
              marcando &&
              marcar.mutate(
                { rotinaId: rotina.id, corpo: { alvo: marcando.alvo, marca: marcando.marca, motivo } },
                { onSuccess: () => setMarcando(null) },
              )
            }
          />
          <DialogoDeExtensao rotina={rotina} aberto={estendendo} aoFechar={() => setEstendendo(false)} />
          {dialogo}
        </>
      ) : null}
    </div>
  );
}
